"use client"

import * as React from "react"
import { LockIcon } from "lucide-react"

import { SurfaceCard, cn } from "@ibada/ui"

import { AlivePillTone, AliveStatusCell } from "@/shared/components/alive-cells"

/**
 * The pieces of the finance settings tabs (boards Configuration 2, Devise 2): a summary card with a soft green
 * tint on top, then a grid of tiles, each a muted label (with a lock while it cannot change), the value large
 * and one muted line. No coloured banners: the summary card says why something is locked.
 */

export enum FinanceSettingsSummarySize {
  Default = "default",
  /** The currency card: a bigger mark and title. */
  Large = "large",
}

export type FinanceSettingsSummaryFigure = {
  id: string
  value: React.ReactNode
  label: React.ReactNode
}

export function FinanceSettingsSummaryCard({
  mark,
  title,
  subtitle,
  pills = [],
  description,
  figures = [],
  trailing,
  size = FinanceSettingsSummarySize.Default,
  testId,
}: {
  /** The round mark on the left: an icon, or a currency symbol. */
  mark: React.ReactNode
  title: React.ReactNode
  /** Muted words right after the title (a currency's name). */
  subtitle?: React.ReactNode
  pills?: Array<{ id: string; tone: AlivePillTone; label: React.ReactNode }>
  description?: React.ReactNode
  /** Key figures on the right (ledger entries, months kept). */
  figures?: FinanceSettingsSummaryFigure[]
  /** Anything else on the right (a "Verrouillée" pill). */
  trailing?: React.ReactNode
  size?: FinanceSettingsSummarySize
  testId?: string
}) {
  const large = size === FinanceSettingsSummarySize.Large
  return (
    <SurfaceCard
      data-testid={testId}
      className='from-primary/10 to-card overflow-hidden bg-gradient-to-r to-55%'
    >
      <div className={cn("flex flex-col gap-4 sm:flex-row sm:items-center", large ? "px-6 py-5" : "px-5 py-4")}>
        <div className='flex min-w-0 flex-1 items-start gap-4 sm:items-center'>
          <span
            aria-hidden
            className={cn(
              "bg-card text-primary flex shrink-0 items-center justify-center rounded-full border font-extrabold",
              large ? "size-16 text-[26px]" : "size-[52px]",
            )}
          >
            {mark}
          </span>
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 flex-wrap items-center gap-2'>
              <span className={cn("font-extrabold", large ? "text-2xl" : "text-xl")}>{title}</span>
              {subtitle ? <span className='text-muted-foreground text-[15px]'>{subtitle}</span> : null}
              {pills.map((pill) => (
                <AliveStatusCell key={pill.id} tone={pill.tone}>
                  {pill.label}
                </AliveStatusCell>
              ))}
            </div>
            {description ? <p className='text-muted-foreground mt-1 text-[13px]'>{description}</p> : null}
          </div>
        </div>
        {figures.length > 0 || trailing ? (
          <div className='flex shrink-0 items-center gap-6 sm:justify-end'>
            {figures.map((figure) => (
              <div key={figure.id} className='sm:text-right' data-testid={`summary-figure-${figure.id}`}>
                <div className='text-xl font-extrabold tabular-nums'>{figure.value}</div>
                <div className='text-muted-foreground text-xs'>{figure.label}</div>
              </div>
            ))}
            {trailing}
          </div>
        ) : null}
      </div>
    </SurfaceCard>
  )
}

export function FinanceSettingsTileGrid({ columns, children }: { columns: number; children: React.ReactNode }) {
  return (
    <div className={cn("grid gap-3 sm:grid-cols-2", columns === 4 ? "xl:grid-cols-4" : "lg:grid-cols-3")}>{children}</div>
  )
}

/**
 * One setting as a tile. With `editor` the tile is editable: the control takes the value's place. With `locked`
 * the label carries a lock (and its reason as the accessible name).
 */
export function FinanceSettingsTile({
  label,
  value,
  hint,
  locked = false,
  lockedLabel,
  editor,
  mark,
  subLabel,
  children,
  testId,
}: {
  label: React.ReactNode
  value?: React.ReactNode
  hint?: React.ReactNode
  locked?: boolean
  lockedLabel?: string
  editor?: React.ReactNode
  /**
   * A round mark before the label (an account's type icon, Soldes d'ouverture 2). With it, the label reads as the
   * tile's bold title and `subLabel` as one muted line under it.
   */
  mark?: React.ReactNode
  subLabel?: React.ReactNode
  /** Anything under the hint (a share bar, a link). */
  children?: React.ReactNode
  testId?: string
}) {
  return (
    <div
      data-testid={testId}
      data-locked={locked ? "true" : undefined}
      className='bg-card flex min-w-0 flex-col gap-1.5 rounded-2xl border px-4 py-3.5'
    >
      {mark ? (
        <span className='flex min-w-0 items-center gap-3'>
          <span
            aria-hidden
            className='bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-full'
          >
            {mark}
          </span>
          <span className='flex min-w-0 flex-1 flex-col'>
            <span className='truncate text-[14px] font-bold'>{label}</span>
            {subLabel ? <span className='text-muted-foreground truncate text-[12.5px]'>{subLabel}</span> : null}
          </span>
          {locked ? (
            <LockIcon className='text-muted-foreground size-3.5 shrink-0 opacity-70' role='img' aria-label={lockedLabel} aria-hidden={!lockedLabel} />
          ) : null}
        </span>
      ) : (
        <span className='text-muted-foreground flex items-center justify-between gap-2 text-[12.5px] font-bold'>
          <span className='truncate'>{label}</span>
          {locked ? (
            <LockIcon className='size-3.5 shrink-0 opacity-70' role='img' aria-label={lockedLabel} aria-hidden={!lockedLabel} />
          ) : null}
        </span>
      )}
      {editor ?? <span className='truncate text-[19px] font-extrabold'>{value}</span>}
      {hint ? <span className='text-muted-foreground text-[12.5px]'>{hint}</span> : null}
      {children}
    </div>
  )
}

/** A tile that only explains (Devise 2): a bold title and one muted line. */
export function FinanceSettingsNoteTile({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className='bg-card min-w-0 rounded-2xl border px-4 py-3.5'>
      <span className='text-[13.5px] font-bold'>{title}</span>
      <p className='text-muted-foreground mt-1 text-[12.5px]'>{children}</p>
    </div>
  )
}
